import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  IconPlus, IconMapPin, IconEdit, IconTrash, IconCalendar, IconUser,
  IconPlayerPlay, IconPlayerStop, IconRoute, IconClock, IconStopwatch,
  IconGauge, IconBolt, IconRun, IconMountain, IconTrendingUp, IconX,
  IconList, IconLayoutGrid,
} from '@tabler/icons-react';
import api from '../api/api';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

const ACTIVITY_TYPES = ['등산', '하이킹', '러닝', '산책'];
const ACTIVITY_TYPE_COLORS = { 등산: 'green', 하이킹: 'orange', 러닝: 'red', 산책: 'blue' };

/** 카카오맵 SDK를 1회만 동적 로드 (appkey는 /auth/oauth-config 에서 조회) */
function loadKakaoMapSdk(appKey) {
  return new Promise((resolve, reject) => {
    if (window.kakao && window.kakao.maps && window.kakao.maps.LatLng) {
      resolve(window.kakao);
      return;
    }
    const existing = document.getElementById('kakao-map-sdk');
    if (existing) {
      existing.addEventListener('load', () => window.kakao.maps.load(() => resolve(window.kakao)));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = 'kakao-map-sdk';
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${appKey}&autoload=false`;
    script.onload = () => window.kakao.maps.load(() => resolve(window.kakao));
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function formatDateDot(dateStr) {
  if (!dateStr) return '-';
  return dateStr.replaceAll('-', '.');
}

function formatDuration(sec) {
  if (sec == null) return '-';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
}

function formatTime(dateTimeStr) {
  if (!dateTimeStr) return '-';
  return dateTimeStr.slice(11, 16);
}

/** 상세 팝업 통계 카드 구성 (아이콘/색상 포함) */
function buildStatCards(a) {
  return [
    { icon: IconPlayerPlay, color: 'green', label: '시작시간', value: formatTime(a.startTime) },
    { icon: IconPlayerStop, color: 'red', label: '종료시간', value: formatTime(a.endTime) },
    { icon: IconRoute, color: 'blue', label: '이동거리', value: `${a.distanceKm}km` },
    { icon: IconClock, color: 'azure', label: '총 소요시간', value: formatDuration(a.totalDurationSec) },
    { icon: IconStopwatch, color: 'cyan', label: '순수 이동시간', value: formatDuration(a.movingDurationSec) },
    { icon: IconGauge, color: 'teal', label: '평균속도', value: `${a.avgSpeedKmh}km/h` },
    { icon: IconBolt, color: 'orange', label: '최고속도', value: `${a.maxSpeedKmh}km/h` },
    { icon: IconRun, color: 'purple', label: '평균 페이스', value: `${a.avgPaceMinPerKm}분/km` },
    { icon: IconMountain, color: 'lime', label: '최고/최저 고도', value: `${a.maxElevation}m / ${a.minElevation}m` },
    { icon: IconTrendingUp, color: 'pink', label: '누적 상승/하강', value: `${a.elevationGain}m / ${a.elevationLoss}m` },
  ];
}

/** 비교 팝업에 표시할 항목(라벨/값 추출 함수) */
const COMPARE_ROWS = [
  { label: '날짜', render: a => formatDateDot(a.activityDate) },
  { label: '구분', render: a => <span className={`badge bg-${ACTIVITY_TYPE_COLORS[a.activityType] || 'blue'}-lt text-${ACTIVITY_TYPE_COLORS[a.activityType] || 'blue'}`}>{a.activityType}</span> },
  { label: '작성자', render: a => a.authorNickname || a.authorName },
  { label: '장소', render: a => a.location || '-' },
  { label: '시작시간', render: a => formatTime(a.startTime) },
  { label: '종료시간', render: a => formatTime(a.endTime) },
  { label: '이동거리', render: a => `${a.distanceKm}km`, numeric: a => a.distanceKm, higherIsBetter: true },
  { label: '총 소요시간', render: a => formatDuration(a.totalDurationSec), numeric: a => a.totalDurationSec, higherIsBetter: false },
  { label: '순수 이동시간', render: a => formatDuration(a.movingDurationSec), numeric: a => a.movingDurationSec, higherIsBetter: false },
  { label: '평균속도', render: a => `${a.avgSpeedKmh}km/h`, numeric: a => a.avgSpeedKmh, higherIsBetter: true },
  { label: '최고속도', render: a => `${a.maxSpeedKmh}km/h`, numeric: a => a.maxSpeedKmh, higherIsBetter: true },
  { label: '평균 페이스', render: a => `${a.avgPaceMinPerKm}분/km`, numeric: a => a.avgPaceMinPerKm, higherIsBetter: false },
  { label: '최고/최저 고도', render: a => `${a.maxElevation}m / ${a.minElevation}m` },
  { label: '누적 상승/하강', render: a => `${a.elevationGain}m / ${a.elevationLoss}m` },
  { label: '메모', render: a => a.memo || '-' },
];

/** 작성자 아바타 (프로필 이미지 없으면 색상 배경 + 이니셜) */
function AuthorAvatar({ src, name, color, size = 26 }) {
  if (src) {
    return (
      <img src={src} alt={name || ''} style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
        border: `1.5px solid ${color || 'var(--tblr-primary)'}`,
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color || 'var(--tblr-primary)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, fontWeight: 700,
    }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

/** 이미지 파일을 지정된 최대 변 길이(px)에 맞춰 비율 유지하며 JPEG로 리사이즈 */
function resizeImageFile(file, maxSize, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const scale = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob); else reject(new Error('이미지 변환 실패'));
      }, 'image/jpeg', quality);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/** 지도 시작/종료 지점을 구분하는 색상별 핀 마커 이미지 생성 */
function createPinMarkerImage(kakao, color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
    <path d="M16 0C7.2 0 0 7.2 0 16c0 11 16 24 16 24s16-13 16-24C32 7.2 24.8 0 16 0z" fill="${color}"/>
    <circle cx="16" cy="16" r="6" fill="#fff"/>
  </svg>`;
  const src = `data:image/svg+xml;base64,${window.btoa(svg)}`;
  return new kakao.maps.MarkerImage(src, new kakao.maps.Size(32, 40), { offset: new kakao.maps.Point(16, 40) });
}

export default function OutdoorActivityPage({ showToast }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState(null); // /outdoor-activities/upload-gpx 응답
  const [form, setForm] = useState({ title: '', activityType: '', location: '', memo: '' });
  const [saving, setSaving] = useState(false);

  const [batchFiles, setBatchFiles] = useState([]); // 다중 선택 시 (2개 이상)
  const [batchActivityType, setBatchActivityType] = useState('');
  const [batchResults, setBatchResults] = useState([]); // [{fileName, status, error}]
  const [batchRunning, setBatchRunning] = useState(false);

  const [selected, setSelected] = useState(null);
  const [track, setTrack] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', activityType: '', location: '', memo: '' });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const [compareIds, setCompareIds] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('outdoor_viewMode') || 'list');
  useEffect(() => { sessionStorage.setItem('outdoor_viewMode', viewMode); }, [viewMode]);

  const [filterYear, setFilterYear] = useState(() => sessionStorage.getItem('outdoor_filterYear') || '');
  const [filterDate, setFilterDate] = useState(() => sessionStorage.getItem('outdoor_filterDate') || '');
  const [filterTitle, setFilterTitle] = useState(() => sessionStorage.getItem('outdoor_filterTitle') || '');
  const [filterAuthor, setFilterAuthor] = useState(() => sessionStorage.getItem('outdoor_filterAuthor') || '');

  useEffect(() => { sessionStorage.setItem('outdoor_filterYear', filterYear); }, [filterYear]);
  useEffect(() => { sessionStorage.setItem('outdoor_filterDate', filterDate); }, [filterDate]);
  useEffect(() => { sessionStorage.setItem('outdoor_filterTitle', filterTitle); }, [filterTitle]);
  useEffect(() => { sessionStorage.setItem('outdoor_filterAuthor', filterAuthor); }, [filterAuthor]);

  const mapRef = useRef(null);
  const kakaoKeyRef = useRef(null);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/outdoor-activities');
      setActivities(res.data);
    } catch {
      showToast('목록을 불러오지 못했습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  /* ── 추가 모달 ── */
  const resetAddModal = async () => {
    if (batchRunning) return; // 진행 중에는 닫지 않음
    if (draft?.fileId) {
      try { await api.delete(`/files/${draft.fileId}`); } catch { /* 무시 */ }
    }
    setDraft(null);
    setForm({ title: '', activityType: '', location: '', memo: '' });
    setBatchFiles([]);
    setBatchActivityType('');
    setBatchResults([]);
    setShowAddModal(false);
  };

  const handleResetFile = async () => {
    if (draft?.fileId) {
      try { await api.delete(`/files/${draft.fileId}`); } catch { /* 무시 */ }
    }
    setDraft(null);
    setForm({ title: '', activityType: '', location: '', memo: '' });
    setBatchFiles([]);
    setBatchActivityType('');
    setBatchResults([]);
  };

  const uploadGpxFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/outdoor-activities/upload-gpx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  const handleGpxSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length === 1) {
      setUploading(true);
      try {
        const data = await uploadGpxFile(files[0]);
        setDraft(data);
        setForm({ title: formatDateDot(data.activityDate), activityType: '', location: '', memo: '' });
      } catch (err) {
        showToast(err?.response?.data?.error || 'GPX 파일 업로드에 실패했습니다.', 'danger');
      } finally {
        setUploading(false);
      }
      return;
    }

    setBatchFiles(files);
    setBatchActivityType('');
    setBatchResults(files.map(f => ({ fileName: f.name, status: 'pending' })));
  };

  /** 다중 파일 일괄 등록: 파일별 업로드+등록을 순차 처리, 제목=날짜/장소·메모는 빈값으로 두고 리스트에서 개별 수정 */
  const handleBatchStart = async () => {
    if (!batchActivityType) { showToast('활동구분을 선택하세요.', 'warning'); return; }
    setBatchRunning(true);
    let successCount = 0;

    for (let i = 0; i < batchFiles.length; i++) {
      setBatchResults(prev => prev.map((r, idx) => (idx === i ? { ...r, status: 'uploading' } : r)));
      try {
        const data = await uploadGpxFile(batchFiles[i]);
        await api.post('/outdoor-activities', {
          gpxFileId: data.fileId,
          title: formatDateDot(data.activityDate),
          activityType: batchActivityType,
          location: '',
          memo: '',
          activityDate: data.activityDate,
          startTime: data.startTime,
          endTime: data.endTime,
          distanceKm: data.distanceKm,
          totalDurationSec: data.totalDurationSec,
          movingDurationSec: data.movingDurationSec,
          avgSpeedKmh: data.avgSpeedKmh,
          maxSpeedKmh: data.maxSpeedKmh,
          avgPaceMinPerKm: data.avgPaceMinPerKm,
          maxElevation: data.maxElevation,
          minElevation: data.minElevation,
          elevationGain: data.elevationGain,
          elevationLoss: data.elevationLoss,
        });
        successCount++;
        setBatchResults(prev => prev.map((r, idx) => (idx === i ? { ...r, status: 'success' } : r)));
      } catch (err) {
        const msg = err?.response?.data?.error || '등록 실패';
        setBatchResults(prev => prev.map((r, idx) => (idx === i ? { ...r, status: 'error', error: msg } : r)));
      }
    }

    setBatchRunning(false);
    showToast(`${batchFiles.length}개 중 ${successCount}개 등록 완료`);
    fetchActivities();
  };

  const handleSave = async () => {
    if (!draft) { showToast('GPX 파일을 첨부하세요.', 'warning'); return; }
    if (!form.title.trim()) { showToast('제목을 입력하세요.', 'warning'); return; }
    if (!form.activityType) { showToast('활동구분을 선택하세요.', 'warning'); return; }

    setSaving(true);
    try {
      await api.post('/outdoor-activities', {
        gpxFileId: draft.fileId,
        title: form.title.trim(),
        activityType: form.activityType,
        location: form.location.trim(),
        memo: form.memo.trim(),
        activityDate: draft.activityDate,
        startTime: draft.startTime,
        endTime: draft.endTime,
        distanceKm: draft.distanceKm,
        totalDurationSec: draft.totalDurationSec,
        movingDurationSec: draft.movingDurationSec,
        avgSpeedKmh: draft.avgSpeedKmh,
        maxSpeedKmh: draft.maxSpeedKmh,
        avgPaceMinPerKm: draft.avgPaceMinPerKm,
        maxElevation: draft.maxElevation,
        minElevation: draft.minElevation,
        elevationGain: draft.elevationGain,
        elevationLoss: draft.elevationLoss,
      });
      showToast('야외활동을 등록했습니다.');
      setDraft(null);
      setForm({ title: '', activityType: '', location: '', memo: '' });
      setShowAddModal(false);
      fetchActivities();
    } catch (err) {
      showToast(err?.response?.data?.error || '등록에 실패했습니다.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ── 상세 레이어 ── */
  const openDetail = async (activity) => {
    setSelected(activity);
    setEditMode(false);
    setEditForm({
      title: activity.title,
      activityType: activity.activityType,
      location: activity.location || '',
      memo: activity.memo || '',
    });
    setTrack(null);

    try {
      const res = await api.get(`/outdoor-activities/${activity.id}/track`);
      setTrack(res.data);
    } catch {
      showToast('트랙 정보를 불러오지 못했습니다.', 'danger');
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setTrack(null);
    setEditMode(false);
  };

  useEffect(() => {
    if (!selected || !track || track.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      try {
        if (!kakaoKeyRef.current) {
          const res = await api.get('/auth/oauth-config');
          kakaoKeyRef.current = res.data?.kakao?.javascriptKey;
        }
        if (!kakaoKeyRef.current) {
          showToast('카카오맵 설정이 없습니다. 관리자에게 문의하세요.', 'warning');
          return;
        }
        const kakao = await loadKakaoMapSdk(kakaoKeyRef.current);
        if (cancelled || !mapRef.current) return;

        const linePath = track.map(p => new kakao.maps.LatLng(p.lat, p.lon));
        const map = new kakao.maps.Map(mapRef.current, { center: linePath[0], level: 5 });

        const polyline = new kakao.maps.Polyline({
          path: linePath,
          strokeWeight: 4,
          strokeColor: '#206bc4',
          strokeOpacity: 0.9,
          strokeStyle: 'solid',
        });
        polyline.setMap(map);

        new kakao.maps.Marker({ position: linePath[0], map, image: createPinMarkerImage(kakao, '#2fb344') });
        new kakao.maps.Marker({ position: linePath[linePath.length - 1], map, image: createPinMarkerImage(kakao, '#d63939') });

        const bounds = new kakao.maps.LatLngBounds();
        linePath.forEach(p => bounds.extend(p));
        map.setBounds(bounds);
      } catch {
        showToast('카카오맵 로드에 실패했습니다.', 'danger');
      }
    })();

    return () => { cancelled = true; };
  }, [selected, track, showToast]);

  const handleEditSave = async () => {
    if (!editForm.title.trim()) { showToast('제목을 입력하세요.', 'warning'); return; }
    if (!editForm.activityType) { showToast('활동구분을 선택하세요.', 'warning'); return; }
    try {
      const res = await api.put(`/outdoor-activities/${selected.id}`, {
        title: editForm.title.trim(),
        activityType: editForm.activityType,
        location: editForm.location.trim(),
        memo: editForm.memo.trim(),
      });
      showToast('수정되었습니다.');
      setSelected(res.data);
      setActivities(prev => prev.map(a => (a.id === res.data.id ? res.data : a)));
      setEditMode(false);
    } catch (err) {
      showToast(err?.response?.data?.error || '수정에 실패했습니다.', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까? 첨부된 GPX 파일도 함께 삭제됩니다.')) return;
    try {
      await api.delete(`/outdoor-activities/${id}`);
      showToast('삭제되었습니다.');
      setActivities(prev => prev.filter(a => a.id !== id));
      setCompareIds(prev => prev.filter(cid => cid !== id));
      if (selected?.id === id) closeDetail();
    } catch (err) {
      showToast(err?.response?.data?.error || '삭제에 실패했습니다.', 'danger');
    }
  };

  /** 사진 추가: 클라이언트에서 400px(원본)/80px(썸네일)로 리사이즈 후 업로드 */
  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0 || !selected) return;

    const remaining = 6 - (selected.photos?.length || 0);
    if (files.length > remaining) {
      showToast(`사진은 최대 6장까지 등록할 수 있습니다. (${remaining}장 추가 가능)`, 'warning');
      return;
    }

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) {
        const [fullBlob, thumbBlob] = await Promise.all([
          resizeImageFile(file, 400),
          resizeImageFile(file, 80),
        ]);
        const baseName = (file.name || 'photo').replace(/\.[^./\\]+$/, '');
        formData.append('full', fullBlob, `${baseName}.jpg`);
        formData.append('thumb', thumbBlob, `${baseName}_thumb.jpg`);
      }
      const res = await api.post(`/outdoor-activities/${selected.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newPhotos = res.data;
      setSelected(s => ({ ...s, photos: [...(s.photos || []), ...newPhotos] }));
      setActivities(prev => prev.map(a => (
        a.id === selected.id ? { ...a, photos: [...(a.photos || []), ...newPhotos] } : a
      )));
      showToast('사진이 추가되었습니다.');
    } catch (err) {
      showToast(err?.response?.data?.error || '사진 업로드에 실패했습니다.', 'danger');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('사진을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/outdoor-activities/${selected.id}/photos/${photoId}`);
      setSelected(s => ({ ...s, photos: s.photos.filter(p => p.id !== photoId) }));
      setActivities(prev => prev.map(a => (
        a.id === selected.id ? { ...a, photos: (a.photos || []).filter(p => p.id !== photoId) } : a
      )));
      showToast('사진이 삭제되었습니다.');
    } catch (err) {
      showToast(err?.response?.data?.error || '사진 삭제에 실패했습니다.', 'danger');
    }
  };

  const toggleCompare = (id) => {
    setCompareIds(prev => (prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]));
  };

  const compareActivities = activities.filter(a => compareIds.includes(a.id));

  const yearOptions = useMemo(() => (
    Array.from(new Set(activities.map(a => a.activityDate?.slice(0, 4)).filter(Boolean))).sort().reverse()
  ), [activities]);

  const filteredActivities = useMemo(() => activities.filter(a => {
    if (filterYear && !a.activityDate?.startsWith(filterYear)) return false;
    if (filterDate && a.activityDate !== filterDate) return false;
    if (filterTitle && !a.title?.toLowerCase().includes(filterTitle.trim().toLowerCase())) return false;
    if (filterAuthor) {
      const authorStr = `${a.authorNickname || ''} ${a.authorName || ''}`.toLowerCase();
      if (!authorStr.includes(filterAuthor.trim().toLowerCase())) return false;
    }
    return true;
  }), [activities, filterYear, filterDate, filterTitle, filterAuthor]);

  const resetFilters = () => {
    setFilterYear('');
    setFilterDate('');
    setFilterTitle('');
    setFilterAuthor('');
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0 d-flex align-items-center gap-2">
          <IconMapPin size={22} /> 야외활동
        </h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary d-flex align-items-center gap-1"
            disabled={compareIds.length < 2} onClick={() => setShowCompareModal(true)}>
            비교{compareIds.length > 0 && ` (${compareIds.length})`}
          </button>
          <button className="btn btn-primary d-flex align-items-center gap-1" onClick={() => setShowAddModal(true)}>
            <IconPlus size={16} /> 추가
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-6 col-md-2">
              <select className="form-select form-select-sm" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                <option value="">전체 년도</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <input type="date" className="form-control form-control-sm" value={filterDate}
                onChange={e => setFilterDate(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <input type="text" className="form-control form-control-sm" placeholder="제목 검색"
                value={filterTitle} onChange={e => setFilterTitle(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <input type="text" className="form-control form-control-sm" placeholder="작성자 검색"
                value={filterAuthor} onChange={e => setFilterAuthor(e.target.value)} />
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={resetFilters}>필터 초기화</button>
              <div className="btn-group" role="group">
                <button type="button"
                  className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  title="목록 보기" onClick={() => setViewMode('list')}>
                  <IconList size={16} />
                </button>
                <button type="button"
                  className={`btn btn-sm ${viewMode === 'gallery' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  title="갤러리 보기" onClick={() => setViewMode('gallery')}>
                  <IconLayoutGrid size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted py-5">불러오는 중...</div>
      ) : filteredActivities.length === 0 ? (
        <div className="text-center text-muted py-5">
          {activities.length === 0 ? '등록된 야외활동 기록이 없습니다.' : '검색 조건에 맞는 기록이 없습니다.'}
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th style={{ width: viewMode === 'gallery' ? 'auto' : 52 }}>사진</th>
                  <th>날짜</th>
                  <th>제목</th>
                  {viewMode === 'list' ? (
                    <>
                      <th>구분</th>
                      <th>작성자</th>
                      <th>거리</th>
                      <th>소요시간</th>
                      <th>평균속도</th>
                    </>
                  ) : (
                    <th>작성자</th>
                  )}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.map(a => (
                  <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(a)}>
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="form-check-input"
                        checked={compareIds.includes(a.id)} onChange={() => toggleCompare(a.id)} />
                    </td>
                    <td>
                      {a.photos?.length > 0 ? (
                        viewMode === 'gallery' ? (
                          <div className="d-flex gap-1">
                            {a.photos.map(p => (
                              <img key={p.id} src={`${api.defaults.baseURL}${p.thumbUrl}`} alt=""
                                style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                            ))}
                          </div>
                        ) : (
                          <img src={`${api.defaults.baseURL}${a.photos[0].thumbUrl}`} alt=""
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                        )
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{formatDateDot(a.activityDate)}</td>
                    <td>{a.title}</td>
                    {viewMode === 'list' ? (
                      <>
                        <td><span className={`badge bg-${ACTIVITY_TYPE_COLORS[a.activityType] || 'blue'}-lt text-${ACTIVITY_TYPE_COLORS[a.activityType] || 'blue'}`}>{a.activityType}</span></td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <AuthorAvatar src={a.authorProfileImage} name={a.authorNickname || a.authorName} color={a.authorColor} />
                            {a.authorNickname || a.authorName}
                          </div>
                        </td>
                        <td>{a.distanceKm != null ? `${a.distanceKm}km` : '-'}</td>
                        <td>{formatDuration(a.totalDurationSec)}</td>
                        <td>{a.avgSpeedKmh != null ? `${a.avgSpeedKmh}km/h` : '-'}</td>
                      </>
                    ) : (
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <AuthorAvatar src={a.authorProfileImage} name={a.authorNickname || a.authorName} color={a.authorColor} />
                          {a.authorNickname || a.authorName}
                        </div>
                      </td>
                    )}
                    <td onClick={e => e.stopPropagation()}>
                      {a.mine && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(a.id)}>
                          <IconTrash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 추가 모달 */}
      {showAddModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 5000 }}
          onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, resetAddModal)}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">야외활동 추가</h5>
                <button type="button" className="btn-close" onClick={resetAddModal} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">GPX 파일</label>
                  {draft ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="form-control-plaintext">{draft.fileName}</span>
                      <button className="btn btn-sm btn-outline-secondary" onClick={handleResetFile}>파일 변경</button>
                    </div>
                  ) : batchFiles.length > 0 ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="form-control-plaintext">{batchFiles.length}개 파일 선택됨</span>
                      {!batchRunning && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={handleResetFile}>파일 변경</button>
                      )}
                    </div>
                  ) : (
                    <input type="file" accept=".gpx" multiple className="form-control" onChange={handleGpxSelect} disabled={uploading} />
                  )}
                  <div className="form-text">Relive, 삼성헬스 등의 사이트/앱에서 GPX 파일을 다운받아 업로드해주세요. 여러 개를 한 번에 선택하면 일괄 등록됩니다.</div>
                  {uploading && <div className="text-muted mt-1">업로드 및 분석 중...</div>}
                </div>

                {batchFiles.length > 0 && (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">활동구분 (전체 파일에 동일 적용)</label>
                      <div className="btn-group d-flex flex-wrap" role="group">
                        {ACTIVITY_TYPES.map(t => (
                          <button key={t} type="button" disabled={batchRunning}
                            className={`btn ${batchActivityType === t ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setBatchActivityType(t)}>
                            {t}
                          </button>
                        ))}
                      </div>
                      <div className="form-text">제목은 각 파일의 날짜로 자동 등록되며, 장소·메모 등 상세 내용은 등록 후 목록에서 개별 항목을 클릭해 수정할 수 있습니다.</div>
                    </div>

                    <div className="mb-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
                      <table className="table table-sm table-vcenter">
                        <tbody>
                          {batchResults.map((r) => (
                            <tr key={r.fileName}>
                              <td className="text-truncate" style={{ maxWidth: 260 }}>{r.fileName}</td>
                              <td className="text-end">
                                {r.status === 'pending' && <span className="text-muted">대기</span>}
                                {r.status === 'uploading' && <span className="text-blue">처리 중...</span>}
                                {r.status === 'success' && <span className="text-green">완료</span>}
                                {r.status === 'error' && <span className="text-red" title={r.error}>실패</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {draft && (
                  <>
                    <div className="row g-2 mb-3">
                      <div className="col-6 col-md-4"><small className="text-muted d-block">시작시간</small>{formatTime(draft.startTime)}</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">종료시간</small>{formatTime(draft.endTime)}</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">이동거리</small>{draft.distanceKm}km</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">총 소요시간</small>{formatDuration(draft.totalDurationSec)}</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">순수 이동시간</small>{formatDuration(draft.movingDurationSec)}</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">평균속도</small>{draft.avgSpeedKmh}km/h</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">최고속도</small>{draft.maxSpeedKmh}km/h</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">평균 페이스</small>{draft.avgPaceMinPerKm}분/km</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">최고/최저 고도</small>{draft.maxElevation}m / {draft.minElevation}m</div>
                      <div className="col-6 col-md-4"><small className="text-muted d-block">누적 상승/하강</small>{draft.elevationGain}m / {draft.elevationLoss}m</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">제목</label>
                      <input type="text" className="form-control" value={form.title}
                        onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">활동구분</label>
                      <div className="btn-group d-flex flex-wrap" role="group">
                        {ACTIVITY_TYPES.map(t => (
                          <button key={t} type="button"
                            className={`btn ${form.activityType === t ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setForm(f => ({ ...f, activityType: t }))}>
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">대표 장소명</label>
                      <input type="text" className="form-control" placeholder="예: 관악산"
                        value={form.location}
                        onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">메모</label>
                      <textarea className="form-control" rows={3} value={form.memo}
                        onChange={e => setForm(f => ({ ...f, memo: e.target.value }))} />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {batchFiles.length > 0 ? (
                  batchResults.length > 0 && batchResults.every(r => r.status === 'success' || r.status === 'error') ? (
                    <button className="btn btn-primary" onClick={resetAddModal}>닫기</button>
                  ) : (
                    <>
                      <button className="btn btn-link" disabled={batchRunning} onClick={resetAddModal}>취소</button>
                      <button className="btn btn-primary" disabled={!batchActivityType || batchRunning} onClick={handleBatchStart}>
                        {batchRunning ? '처리 중...' : `일괄 등록 시작 (${batchFiles.length}개)`}
                      </button>
                    </>
                  )
                ) : (
                  <>
                    <button className="btn btn-link" onClick={resetAddModal}>취소</button>
                    <button className="btn btn-primary" disabled={!draft || saving} onClick={handleSave}>
                      {saving ? '저장 중...' : '저장'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 상세 레이어 */}
      {selected && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 5000 }}
          onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, closeDetail)}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content overflow-hidden">
              <div className={`px-4 pt-4 pb-3 border-bottom bg-${ACTIVITY_TYPE_COLORS[selected.activityType] || 'blue'}-lt`}>
                <div className="d-flex align-items-start justify-content-between gap-2">
                  {editMode ? (
                    <input type="text" className="form-control me-2" value={editForm.title}
                      onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                  ) : (
                    <h3 className="mb-0 fw-bold">{selected.title}</h3>
                  )}
                  <button type="button" className="btn-close" onClick={closeDetail} />
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
                  <div className="d-flex align-items-center flex-wrap gap-3">
                    {editMode ? (
                      <div className="btn-group" role="group">
                        {ACTIVITY_TYPES.map(t => (
                          <button key={t} type="button"
                            className={`btn btn-sm ${editForm.activityType === t ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setEditForm(f => ({ ...f, activityType: t }))}>
                            {t}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className={`badge bg-${ACTIVITY_TYPE_COLORS[selected.activityType] || 'blue'} text-white`}>{selected.activityType}</span>
                    )}
                    <span className="text-muted d-flex align-items-center gap-1">
                      <IconCalendar size={14} />{formatDateDot(selected.activityDate)}
                    </span>
                    <span className="text-muted d-flex align-items-center gap-1">
                      <IconUser size={14} />{selected.authorNickname || selected.authorName}
                    </span>
                    {editMode ? (
                      <input type="text" className="form-control form-control-sm" style={{ maxWidth: 200 }}
                        placeholder="대표 장소명" value={editForm.location}
                        onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
                    ) : selected.location && (
                      <span className="text-muted d-flex align-items-center gap-1">
                        <IconMapPin size={14} />{selected.location}
                      </span>
                    )}
                  </div>
                  {selected.mine && (
                    editMode ? (
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-primary" onClick={handleEditSave}>저장</button>
                        <button className="btn btn-sm btn-link" onClick={() => setEditMode(false)}>취소</button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditMode(true)}>
                          <IconEdit size={14} /> 수정
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(selected.id)}>
                          <IconTrash size={14} /> 삭제
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="modal-body">
                <div className="row row-cols-2 row-cols-lg-5 g-2 mb-3">
                  {buildStatCards(selected).map(({ icon: Icon, color, label, value }) => (
                    <div className="col" key={label}>
                      <div className="card card-sm bg-light border-0 h-100">
                        <div className="card-body text-center py-3">
                          <Icon size={20} className={`text-${color} mb-1`} />
                          <div className="text-muted small">{label}</div>
                          <div className="fw-bold">{value}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {(selected.photos?.length > 0 || editMode) && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">사진 {selected.photos?.length > 0 && `(${selected.photos.length}/6)`}</label>
                  <div className="d-flex flex-wrap gap-2">
                    {(selected.photos || []).map(p => (
                      <div key={p.id} className="position-relative" style={{ width: 72, height: 72 }}>
                        <img src={`${api.defaults.baseURL}${p.thumbUrl}`} alt=""
                          onClick={() => setLightboxUrl(`${api.defaults.baseURL}${p.url}`)}
                          className="border rounded"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} />
                        {editMode && selected.mine && (
                          <button type="button"
                            className="btn btn-sm btn-danger position-absolute p-0 d-flex align-items-center justify-content-center"
                            style={{ width: 20, height: 20, top: -6, right: -6, borderRadius: '50%' }}
                            onClick={() => handleDeletePhoto(p.id)}>
                            <IconX size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    {editMode && selected.mine && (selected.photos?.length || 0) < 6 && (
                      <label className="d-flex align-items-center justify-content-center border rounded text-muted mb-0"
                        style={{
                          width: 72, height: 72,
                          cursor: photoUploading ? 'default' : 'pointer',
                          opacity: photoUploading ? 0.5 : 1,
                        }}>
                        {photoUploading
                          ? <span className="spinner-border spinner-border-sm" />
                          : <IconPlus size={22} />}
                        <input type="file" accept="image/*" multiple hidden
                          disabled={photoUploading} onChange={handlePhotoSelect} />
                      </label>
                    )}
                  </div>
                </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">메모</label>
                  {editMode ? (
                    <textarea className="form-control" rows={3} value={editForm.memo}
                      onChange={e => setEditForm(f => ({ ...f, memo: e.target.value }))} />
                  ) : (
                    <div className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{selected.memo || '-'}</div>
                  )}
                </div>

                <div className="rounded overflow-hidden border">
                  <div ref={mapRef} style={{ width: '100%', height: 420, background: '#f1f3f5' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 사진 라이트박스 */}
      {lightboxUrl && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.8)', zIndex: 6000 }}
          onClick={() => setLightboxUrl(null)}>
          <div className="modal-dialog modal-dialog-centered">
            <img src={lightboxUrl} alt="" style={{ maxWidth: '100%', maxHeight: '90vh', margin: '0 auto', display: 'block' }} />
          </div>
        </div>
      )}

      {/* 비교 레이어 */}
      {showCompareModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 5000 }}
          onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setShowCompareModal(false))}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">야외활동 비교</h5>
                <button type="button" className="btn-close" onClick={() => setShowCompareModal(false)} />
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-vcenter" style={{ whiteSpace: 'nowrap' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 140 }}>항목</th>
                        {COMPARE_ROWS.map(row => <th key={row.label} style={{ fontSize: '0.75rem' }}>{row.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const bestByLabel = {};
                        const worstByLabel = {};
                        COMPARE_ROWS.forEach(row => {
                          if (row.numeric && compareActivities.length >= 2) {
                            const values = compareActivities.map(row.numeric).filter(v => v != null && !Number.isNaN(v));
                            const max = Math.max(...values);
                            const min = Math.min(...values);
                            if (max !== min) {
                              bestByLabel[row.label] = row.higherIsBetter ? max : min;
                              worstByLabel[row.label] = row.higherIsBetter ? min : max;
                            }
                          }
                        });
                        return compareActivities.map(a => (
                          <tr key={a.id}>
                            <td className="fw-semibold text-muted" style={{ fontSize: '0.75rem' }}>{a.title}</td>
                            {COMPARE_ROWS.map(row => {
                              const v = row.numeric ? row.numeric(a) : undefined;
                              const isBest = bestByLabel[row.label] !== undefined && v === bestByLabel[row.label];
                              const isWorst = worstByLabel[row.label] !== undefined && v === worstByLabel[row.label];
                              return (
                                <td key={row.label}
                                  className={isBest ? 'bg-red-lt text-red fw-bold' : isWorst ? 'bg-blue-lt text-blue' : undefined}
                                  style={{ fontSize: '0.75rem', ...(row.label === '메모' ? { whiteSpace: 'pre-wrap' } : null) }}>
                                  {row.render(a)}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
